import { classed } from "@/lib/classed";
import { Icon } from "./icons";
import { Button } from "./button";
import { cx } from "@/lib/design-tokens";
import { HStack } from "./h-stack";
import { VStack } from "./v-stack";
import { Surface } from "./surface";

/**
 * Label Component
 * Used for field labels, uppercase styling.
 *
 * Variants:
 * - `variant="form"` — standard spacing for form-field labels:
 *   `size=sm` + `mb-1` baked in. Use this for labels that sit directly
 *   above an input/select so every field has identical label-to-input gap.
 * - `variant="plain"` (default) — no spacing; caller owns size + margin.
 */
export const Label = classed("div", {
  base: "font-medium text-[color:var(--on-surface-variant)] uppercase",
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
    },
    variant: {
      plain: "",
      form: "text-sm mb-1",
    },
  },
  defaultVariants: {
    size: "xs",
    variant: "plain",
  },
});

/**
 * Value Component
 * Used for displaying field values
 */
export const Value = classed("div", {
  base: "font-medium text-[color:var(--on-surface)]",
  variants: {
    size: {
      sm: "text-sm",
      base: "text-base",
      lg: "text-lg",
    },
    color: {
      default: "text-[color:var(--on-surface)]",
      primary: "text-[color:var(--primary)]",
      success: "text-[color:var(--success)]",
      danger: "text-[color:var(--error)]",
      muted: "text-[color:var(--on-surface-variant)]",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
  },
  defaultVariants: {
    size: "base",
    color: "default",
    weight: "medium",
  },
});

/**
 * CardTitle Component
 * Used for card/section headers (smaller subsection titles)
 */
export const CardTitle = classed("h3", {
  base: "font-semibold text-[color:var(--on-surface-variant)] uppercase",
  variants: {
    size: {
      sm: "text-xs",
      base: "text-sm",
    },
  },
  defaultVariants: {
    size: "base",
  },
});

/**
 * PageHeading Component
 * Visible on-page heading (h1). For setting the header bar title, use PageTitle from contexts/page-title-context.
 */
export const PageHeading = classed("h1", {
  base: "font-bold text-[color:var(--on-surface)]",
  variants: {
    size: {
      lg: "text-lg",
      xl: "text-xl",
      "2xl": "text-2xl",
      "3xl": "text-3xl",
    },
  },
  defaultVariants: {
    size: "2xl",
  },
});

/**
 * PageDescription Component
 * Used for page subtitles/descriptions
 */
export const PageDescription = classed("p", {
  base: "text-[color:var(--on-surface-variant)]",
  variants: {
    size: {
      sm: "text-sm",
      base: "text-base",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

/**
 * InfoRow Component
 * Used for icon + label + value pattern
 */
export const InfoRow = ({
  icon,
  label,
  value,
  valueClassName,
  className,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
  className?: string;
}) => (
  <HStack align="start" gap="lg" className={className}>
    {icon && (
      <Text color="muted" className="mt-0.5 flex-shrink-0">
        {icon}
      </Text>
    )}
    <VStack gap="none" fill>
      <Label>{label}</Label>
      <Value className={valueClassName}>{value}</Value>
    </VStack>
  </HStack>
);

/**
 * DataPair Component
 * Used for horizontal label-value pairs
 */
export const DataPair = ({
  label,
  value,
  valueClassName,
  className,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
  className?: string;
}) => (
  <HStack justify="between" className={className}>
    <Label>{label}</Label>
    <Value className={valueClassName}>{value}</Value>
  </HStack>
);

/**
 * FieldGroup Component
 * Used for vertical label + value groups
 */
export const FieldGroup = ({
  label,
  value,
  valueClassName,
  className,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
  className?: string;
}) => (
  <VStack gap="xs" className={className}>
    <Label>{label}</Label>
    <Value className={valueClassName}>{value}</Value>
  </VStack>
);

/**
 * EmptyState Component
 * Used for empty data states
 */
export const EmptyState = ({
  icon,
  title,
  description,
  action,
  role = "status",
  ariaLive = "polite",
  className,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  role?: "status" | "alert" | "none";
  ariaLive?: "polite" | "assertive" | "off";
  className?: string;
}) => (
  <VStack
    align="center"
    role={role === "none" ? undefined : role}
    aria-live={role === "none" ? undefined : ariaLive}
    aria-atomic={role === "none" ? undefined : true}
    className={cx("py-12", className)}
  >
    {icon && (
      <Surface
        bg="background"
        rounded="full"
        className="inline-flex items-center justify-center w-16 h-16 mb-4"
      >
        <Text color="muted">{icon}</Text>
      </Surface>
    )}
    <Text size="lg" weight="semibold" align="center" className="mb-2">
      {title}
    </Text>
    {description && (
      <Text color="muted" align="center" className="max-w-sm mx-auto mb-6">
        {description}
      </Text>
    )}
    {action}
  </VStack>
);

/**
 * ErrorState Component
 * Used for error states
 */
export const ErrorState = ({
  title = "Something went wrong",
  description,
  action,
  onRetry,
  retryLabel = "Retry",
  role = "alert",
  ariaLive = "assertive",
  className,
  ...rest
}: {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  role?: "status" | "alert" | "none";
  ariaLive?: "polite" | "assertive" | "off";
  className?: string;
  "data-testid"?: string;
}) => (
  <VStack
    align="center"
    justify="center"
    gap="md"
    role={role === "none" ? undefined : role}
    aria-live={role === "none" ? undefined : ariaLive}
    aria-atomic={role === "none" ? undefined : true}
    className={className}
    {...rest}
  >
    <Surface
      bg="error-container"
      rounded="full"
      className="w-16 h-16 flex items-center justify-center"
    >
      <Icon.ExclamationTriangle size="xl" color="danger" />
    </Surface>
    <Text size="lg" weight="medium" align="center">
      {title}
    </Text>
    {description && (
      <Text size="sm" color="muted" align="center">
        {description}
      </Text>
    )}
    {action}
    {onRetry && !action && (
      <Button color="neutral" variant="outline" onClick={onRetry}>
        {retryLabel}
      </Button>
    )}
  </VStack>
);

/**
 * Text — inline text element with orthogonal size × weight × color axes.
 * No axis conflicts with another — no !important needed.
 * Supports `as` prop: `<Text as="p">` renders a `<p>`.
 *
 * Common patterns:
 *   <Text>                                        — body (default)
 *   <Text weight="medium">                        — body-medium
 *   <Text color="muted">                          — body-muted
 *   <Text size="xs" color="muted">                — caption
 *   <Text size="xs" weight="medium" color="muted"> — caption-medium
 *   <Text color="danger">                          — error text
 *   <Text size="label" weight="medium" color="muted"> — section label
 *   <Text weight="medium" color="primary">        — link
 *   <Text size="lg" weight="bold" color="primary"> — price
 */
export const Text = classed("span", {
  base: "font-normal text-left text-[color:var(--on-surface)]",
  variants: {
    size: {
      xs: "text-xs",
      sm: "text-sm",
      base: "text-base",
      lg: "text-lg",
      label: "text-[10px] uppercase tracking-widest",
    },
    weight: {
      normal: "",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    color: {
      default: "",
      muted: "text-[color:var(--on-surface-variant)]",
      danger: "text-[color:var(--error)]",
      primary: "text-[color:var(--primary)]",
      success: "text-[color:var(--success)]",
      warning: "text-[color:var(--warning)]",
      info: "text-[color:var(--info)]",
      "accent-orange": "text-[color:var(--accent-orange)]",
      "accent-yellow": "text-[color:var(--accent-yellow)]",
      "on-primary": "text-[color:var(--on-primary)]",
      inherit: "text-inherit",
    },
    align: {
      left: "",
      center: "text-center",
      right: "text-right",
    },
    block: {
      true: "block",
    },
    truncate: {
      true: "truncate block min-w-0",
    },
    font: {
      sans: "",
      mono: "font-mono",
    },
    lineClamp: {
      1: "line-clamp-1",
      2: "line-clamp-2",
      3: "line-clamp-3",
    },
  },
  defaultVariants: {
    size: "sm",
    weight: "normal",
    color: "default",
  },
});

/**
 * RankBadge Component
 * For numbered rankings (1st, 2nd, 3rd, etc.)
 */
export const RankBadge = ({
  rank,
  className,
}: {
  rank: number;
  className?: string;
}) => {
  const colors: Record<number, string> = {
    1: "bg-[color:var(--primary)] text-[color:var(--on-primary)]",
    2: "bg-[color:var(--primary)] text-[color:var(--on-primary)] opacity-80",
    3: "bg-[color:var(--primary)] text-[color:var(--on-primary)] opacity-60",
  };

  return (
    <span
      className={cx(
        "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
        colors[rank] ||
          "bg-[color:var(--outline-variant)] text-[color:var(--on-surface-variant)]",
        className,
      )}
    >
      {rank}
    </span>
  );
};
